import { Component, HostListener, Input, inject, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LogoComponent, ButtonComponent, IconComponent, IconContainerComponent, LogoVariant } from '@mentebonita/ui';
import { AuthService } from '@mentebonita/core';
import { LocaleService } from '../../../shared/services/locale.service';
import { DataLayerService } from '../../../shared/services/datalayer.service';

interface Country {
  code: string;
  name: string;
  label: string;
  flag: string;
}

export type NavbarMode = 'light' | 'dark';

@Component({
  selector: 'layout-navbar',
  standalone: true,
  imports: [CommonModule, RouterLink, LogoComponent, ButtonComponent, IconComponent, IconContainerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.css'],
})
export class LayoutNavbarComponent implements OnInit {
  private authService = inject(AuthService);
  private router = inject(Router);
  private localeService = inject(LocaleService);
  private dataLayer = inject(DataLayerService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  /**
   * Navbar mode - controls background, text colors, and logo variant
   * - light: Light background with blue logo (default)
   * - dark: Dark background with white logo
   */
  @Input() mode: NavbarMode = 'light';

  // Dropdown states
  isServiciosOpen = false;
  isExploraOpen = false;
  isCountryOpen = false;
  isMobileMenuOpen = false;

  // Countries data
  countries: Country[] = [
    {
      code: 'pe',
      name: 'Perú',
      label: 'Quedarse en el sitio de Perú',
      flag: '/assets/flags/pe.svg',
    },
    {
      code: 'mx',
      name: 'México',
      label: 'Cambiar al sitio de México',
      flag: '/assets/flags/mx.svg',
    },
  ];

  selectedCountry: Country = this.countries[0]; // Default to Peru

  ngOnInit(): void {
    this.localeService.country$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(countryCode => {
        const country = this.countries.find(c => c.code === countryCode.toLowerCase());
        if (country) {
          this.selectedCountry = country;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Get logo variant based on mode
   */
  get logoVariant(): LogoVariant {
    return this.mode === 'dark' ? 'white' : 'blue';
  }

  /**
   * Get home link based on selected country
   */
  get homeLink(): string {
    return this.selectedCountry.code === 'mx' ? '/mx' : '/';
  }

  /**
   * Check if user is logged in
   */
  get isLoggedIn(): boolean {
    return this.authService.isLoggedIn();
  }

  /**
   * Navigate to account
   */
  goToAccount(): void {
    this.router.navigate(['/account/home']);
    this.closeMobileMenu();
  }

  /**
   * Logout user
   */
  logout(): void {
    this.authService.logout();
    this.router.navigate(['/']);
    this.closeMobileMenu();
  }

  /**
   * Toggle Servicios dropdown
   */
  toggleServiciosDropdown(): void {
    this.isServiciosOpen = !this.isServiciosOpen;
    if (this.isServiciosOpen) {
      this.isExploraOpen = false;
      this.isCountryOpen = false;
    }
  }

  /**
   * Toggle Explora dropdown
   */
  toggleExploraDropdown(): void {
    this.isExploraOpen = !this.isExploraOpen;
    if (this.isExploraOpen) {
      this.isServiciosOpen = false;
      this.isCountryOpen = false;
    }
  }

  /**
   * Toggle Country dropdown
   */
  toggleCountryDropdown(): void {
    this.isCountryOpen = !this.isCountryOpen;
    if (this.isCountryOpen) {
      this.isServiciosOpen = false;
      this.isExploraOpen = false;
    }
  }

  /**
   * Toggle mobile menu
   */
  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    // Prevent body scroll when mobile menu is open
    if (this.isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }

  /**
   * Close all dropdowns
   */
  closeAllDropdowns(): void {
    this.isServiciosOpen = false;
    this.isExploraOpen = false;
    this.isCountryOpen = false;
  }

  /**
   * Close mobile menu
   */
  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
    document.body.style.overflow = '';
  }

  /**
   * Select country
   */
  private readonly countryRoutes: Record<string, Record<string, string>> = {
    '/': { pe: '/', mx: '/mx' },
    '/mx': { pe: '/', mx: '/mx' },
    '/planes': { pe: '/planes', mx: '/mx/planes' },
    '/mx/planes': { pe: '/planes', mx: '/mx/planes' },
  };

  selectCountry(country: Country): void {
    this.selectedCountry = country;
    this.isCountryOpen = false;
    this.localeService.setCountry(country.code.toUpperCase());

    const currentPath = this.router.url.split('?')[0];
    const routeMap = this.countryRoutes[currentPath];
    if (routeMap) {
      const target = routeMap[country.code];
      if (target && target !== currentPath) {
        this.router.navigateByUrl(target);
      }
    }
  }

  /**
   * Close dropdowns when clicking outside (ESC key)
   */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closeAllDropdowns();
    this.closeMobileMenu();
  }

  /**
   * Close mobile menu on window resize to desktop
   */
  @HostListener('window:resize')
  onWindowResize(): void {
    if (window.innerWidth > 768 && this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  /**
   * Track CTA click - Comenzar Test (Desktop)
   */
  onComenzarTestClick(): void {
    this.dataLayer.trackCtaClick('header');
  }

  /**
   * Track CTA click - Comenzar Test (Mobile)
   */
  onComenzarTestMobileClick(): void {
    this.dataLayer.trackCtaClick('header_mobile');
  }
}
